// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Formatting helpers.
 *
 * VRAM is shown in binary units (GiB/MiB) because that is what llama.cpp,
 * nvidia-smi and every GPU spec sheet actually mean, and mixing GB with GiB is
 * a 7% error — enough to turn "fits" into "doesn't". File sizes are shown the
 * same way for consistency, with the unit spelled out every time.
 */

const KIB = 1024;
const MIB = 1024 * 1024;
const GIB = 1024 * 1024 * 1024;

/** Bytes → a binary-unit string, e.g. "4.68 GiB". */
export function bytesToGiB(bytes: number, digits = 2): string {
  return `${(bytes / GIB).toFixed(digits)} GiB`;
}

export function bytesToMiB(bytes: number, digits = 0): string {
  return `${(bytes / MIB).toFixed(digits)} MiB`;
}

/** Bytes → the largest sensible binary unit. */
export function formatBytes(bytes: number, digits?: number): string {
  const abs = Math.abs(bytes);
  if (abs >= GIB) return `${(bytes / GIB).toFixed(digits ?? 2)} GiB`;
  if (abs >= MIB) return `${(bytes / MIB).toFixed(digits ?? 1)} MiB`;
  if (abs >= KIB) return `${(bytes / KIB).toFixed(digits ?? 1)} KiB`;
  return `${bytes} B`;
}

/** GiB → bytes, for the VRAM input. */
export function gibToBytes(gib: number): number {
  return Math.round(gib * GIB);
}

/** 8192 → "8k", 131072 → "128k", 1500 → "1,500". */
export function formatContext(n: number): string {
  if (n >= 1024 && n % 1024 === 0) return `${n / 1024}k`;
  return n.toLocaleString('en');
}

/** 8030261248 → "8.03 B", 494032768 → "494 M". */
export function formatParams(n: number): string {
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)} T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)} M`;
  return n.toLocaleString('en');
}

export function formatPercent(fraction: number, digits = 1): string {
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Bits per weight, e.g. 4.83 → "4.83 bpw". */
export function formatBpw(bpw: number): string {
  return `${bpw.toFixed(2)} bpw`;
}

/** A compact, human duration for the "read in 1.2 s" line. */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

/** Escape a string for safe insertion into HTML text or an attribute. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Quote a path for a POSIX shell only when it needs it, so the common case
 * stays copy-pasteable and readable.
 */
export function shellQuote(s: string): string {
  if (s === '') return "''";
  if (/^[A-Za-z0-9_@%+=:,./-]+$/.test(s)) return s;
  return `'${s.replace(/'/g, `'\\''`)}'`;
}

/** A filename-safe slug for downloads. */
export function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/\.gguf$/, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'model'
  );
}
